import { classNames } from 'primereact/utils';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { DeclaredUnit } from '../../../../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../../../../shared/interfaces/IFootprint';
import { IWasteTreatment, WASTE_TREATMENT_CATEGORIES } from '../../../../../../PcfWizard.type';
import styles from './WasteTreatmentDropdown.module.css';

type MenuProps = {
  filteredWasteTreatments: Partial<IWasteTreatment>[];
  selectedId?: number;
  onItemSelect: (item: Partial<IWasteTreatment>) => void;
  onItemMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
};

type WasteTreatmentGroup = {
  category: WASTE_TREATMENT_CATEGORIES | '';
  items: Partial<IWasteTreatment>[];
};

const Menu = ({
  filteredWasteTreatments,
  selectedId,
  onItemMouseDown,
  onItemSelect,
}: MenuProps): JSX.Element => {
  const { t } = useTranslation();

  const getItemLabel = (wasteTreatment: Partial<IWasteTreatment>) => {
    return <span className='white-space-normal'>{wasteTreatment.name}</span>;
  };

  // The backend already returns the list sorted by category then name (PAC-1613), so grouping
  // by contiguous runs here keeps that order instead of re-sorting against it.
  const groups = useMemo((): WasteTreatmentGroup[] => {
    const result: WasteTreatmentGroup[] = [];

    filteredWasteTreatments.forEach((item) => {
      const category = item.category ?? '';
      const lastGroup = result[result.length - 1];

      if (lastGroup && lastGroup.category === category) {
        lastGroup.items.push(item);
      } else {
        result.push({ category, items: [item] });
      }
    });

    return result;
  }, [filteredWasteTreatments]);

  return (
    <div className={classNames('relative', styles.menuRoot)} onMouseDown={onItemMouseDown}>
      <div className='p-dropdown-panel p-component p-ripple-disabled p-connected-overlay-enter-done z-2 absolute w-full overflow-auto'>
        <div className='p-dropdown-items-wrapper max-h-15rem'>
          <ul className='p-dropdown-items'>
            {groups.length > 0 ? (
              <>
                {groups.map((group) => {
                  return (
                    <Fragment key={`group-${group.category}`}>
                      <li
                        className={classNames('p-dropdown-item-group', styles['category-header'])}
                      >
                        {group.category}
                      </li>
                      {group.items.map((wasteTreatment, index) => {
                        return (
                          <li
                            key={wasteTreatment.id || index}
                            className={classNames('p-dropdown-item', styles['item-row'], {
                              'p-highlight': wasteTreatment.id === selectedId,
                            })}
                            onClick={(): void => {
                              return onItemSelect(wasteTreatment);
                            }}
                          >
                            <div className='flex justify-content-between w-full'>
                              <div className='flex w-8'>{getItemLabel(wasteTreatment)}</div>
                              <div className='font-light text-gray-600'>
                                <EmissionValueWithSuffix
                                  emissionValue={wasteTreatment?.pcfExcludingBiogenic}
                                  declaredUnit={DeclaredUnit.kilogram}
                                  unitaryProductAmount={1}
                                  emissionSymbol={EmissionSymbol.KG_CO2E}
                                  abbreviateUnit={true}
                                  emissionValueDecimals={6}
                                  numericalValueTextClassName='font-light text-gray-600'
                                  suffixTextClassName='font-light text-gray-600'
                                />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </>
            ) : (
              <li className='p-dropdown-item cursor-auto'>
                <div className='flex w-full'>
                  <span>
                    {t(
                      'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.noWasteTreatmentsFound',
                    )}
                  </span>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Menu;
